import { Button, Flex, Group, Stack, Text } from "@mantine/core";

import { withPolymorphicClickAnalytics } from "@/analytics";
import { WrappingCountdownText } from "@/call-controls";
import { useCallbacksFormStore } from "@/call-controls/ScheduleCallback/callbacksForm.store";
import { useScheduleCallback } from "@/call-controls/ScheduleCallback/useScheduleCallback";
import { callbackActionConfig } from "@/call-controls/ScheduleCallback/utilities";
import { Warning } from "@/common-ui";
import { usePartnerFeatureConfigured } from "@/partner/usePartnerConfig";
import { useAsyncLoading } from "@/shared-utils";

import { useSessionSummaryStore } from "../store/useSessionSummaryStore";
import styles from "./EditSessionSummaryModal.module.css";
import { CALLBACK_CUTOFF } from "./utils";

function Countdown({ expirySeconds }: { expirySeconds: number }) {
    return (
        <WrappingCountdownText
            appendUnit={false}
            format="ss"
            ff="monospace"
            fw="var(--mantine-font-weight-normal)"
            size="xs"
            totalSeconds={expirySeconds}
        />
    );
}

interface SessionSummaryActionsProps {
    onBackClick: () => void;
    afterSubmitClickDefault: () => void;
    getButtonText: () => string;
    setView: (view: "edit-summary" | "schedule-callback") => void;
    endSession: () => Promise<void>;
    summaryUnavailable: boolean;
    shouldCopy: boolean;
    expirySeconds: number;
    view: "edit-summary" | "schedule-callback";
    callbackRemaining: number;
}

export function SessionSummaryActions({
    onBackClick,
    afterSubmitClickDefault,
    getButtonText,
    setView,
    endSession,
    summaryUnavailable,
    shouldCopy,
    expirySeconds,
    view,
    callbackRemaining,
}: SessionSummaryActionsProps) {
    return view === "schedule-callback" ? (
        <SummaryCallbackActions
            onBackClick={onBackClick}
            callbackRemaining={callbackRemaining}
            afterSubmitClickDefault={afterSubmitClickDefault}
            expirySeconds={expirySeconds}
        />
    ) : (
        <SummaryEditActions
            getButtonText={getButtonText}
            callbackRemaining={callbackRemaining}
            setView={setView}
            endSession={endSession}
            summaryUnavailable={summaryUnavailable}
            shouldCopy={shouldCopy}
            expirySeconds={expirySeconds}
        />
    );
}

interface SummaryCallbackActionsProps {
    onBackClick: () => void;
    afterSubmitClickDefault: () => void;
    expirySeconds: number;
    callbackRemaining: number;
}

function SummaryCallbackActions({ onBackClick, afterSubmitClickDefault, expirySeconds }: SummaryCallbackActionsProps) {
    const AnalyticsButton = withPolymorphicClickAnalytics(Button<"button">, "Click");

    const callbackType = useCallbacksFormStore((state) => state.callbackType);
    const selectedCallbackNowDelay = useCallbacksFormStore((state) => state.selectedCallbackNowDelay);
    const { scheduleCallback, canScheduleOrUpdateCallback } = useScheduleCallback();
    const [isSubmitting, onSubmit] = useAsyncLoading(async () => {
        await scheduleCallback();
        afterSubmitClickDefault();
    });

    const reachedCallbackCutoff = expirySeconds <= CALLBACK_CUTOFF;

    return (
        <Stack>
            <Group id="callback-actions" justify="flex-end">
                <AnalyticsButton
                    data-testid={callbackActionConfig.back.testId}
                    analytics={{
                        actionId: callbackActionConfig.back.actionId,
                    }}
                    size="xs"
                    title="Back"
                    onClick={onBackClick}
                    color="gray.0"
                    variant="subtle"
                >
                    <Text fz="xs" td="underline">
                        Back
                    </Text>
                </AnalyticsButton>

                <AnalyticsButton
                    data-testid={callbackActionConfig.schedule.testId}
                    analytics={{
                        actionId: callbackActionConfig.schedule.actionId,
                        eventData: {
                            callbackType,
                            callbackDelay: selectedCallbackNowDelay,
                        },
                    }}
                    className={styles.summaryMainButton}
                    title="Schedule callback"
                    disabled={!canScheduleOrUpdateCallback || isSubmitting || reachedCallbackCutoff}
                    loading={isSubmitting}
                    fz="xs"
                    size="xs"
                    onClick={() => void onSubmit()}
                    variant="filled"
                >
                    Schedule (<Countdown expirySeconds={expirySeconds} />)
                </AnalyticsButton>
            </Group>
            {reachedCallbackCutoff && (
                <Group gap={5} justify="flex-end">
                    <Warning width="10" fill="var(--mantine-color-red-3)" />
                    <Text pl={0} fz="xs" c="red.3">
                        Sorry, there’s not enough time to schedule a callback
                    </Text>
                </Group>
            )}
        </Stack>
    );
}

interface SummaryEditActionsProps {
    getButtonText: () => string;
    setView: (view: "edit-summary" | "schedule-callback") => void;
    endSession: () => Promise<void>;
    summaryUnavailable: boolean;
    callbackRemaining: number;
    shouldCopy: boolean;
    expirySeconds: number;
}

function SummaryEditActions({
    getButtonText,
    setView,
    endSession,
    summaryUnavailable,
    expirySeconds,
    callbackRemaining,
}: SummaryEditActionsProps) {
    const CallbackCustomerButton = withPolymorphicClickAnalytics(Button<"button">, "Click");
    const SendSummaryButton = withPolymorphicClickAnalytics(Button<"button">, "Click");
    const { callbackEnabled: isCallbackEnabled } = usePartnerFeatureConfigured();

    const resolutionStatus = useSessionSummaryStore((state) => state.resolutionStatus);

    function handleScheduleCallbackClick() {
        setView("schedule-callback");
    }

    return (
        <Flex id="modal-actions" justify="flex-end" pt="xl" gap="xs">
            {isCallbackEnabled && callbackRemaining ? (
                <CallbackCustomerButton
                    analytics={{
                        actionId: "SessionSummaryModal_CallCustomerButton",
                    }}
                    className={styles.callbackButton}
                    size="xs"
                    color="white"
                    onClick={handleScheduleCallbackClick}
                    variant="transparent"
                >
                    <Text fw="lighter" fz="xs">
                        Callback
                    </Text>
                </CallbackCustomerButton>
            ) : null}
            <SendSummaryButton
                analytics={{
                    actionId: "SessionSummaryModal_SendButton",
                }}
                className={styles.summaryMainButton}
                aria-label={getButtonText()}
                disabled={summaryUnavailable || resolutionStatus === null}
                size="xs"
                fz="xs"
                onClick={() => void endSession()}
                data-testid="call-controls-end-session"
            >
                {getButtonText()} (<Countdown expirySeconds={expirySeconds} />)
            </SendSummaryButton>
        </Flex>
    );
}
